
import { Component, Mixins } from 'vue-property-decorator'
import StepContainer from '@/components/processing_steps/commons/StepContainer.vue'
import ProcessingStepMixin from '@/components/processing_steps/ProcessingStepMixin.vue'
import FormContainer from '@/components/processing_steps/commons/forms/FormContainer.vue'
import ProjectAdditionalStepMixin from "@/components/processing_steps/project/mixins/ProjectAdditionalStepMixin.vue"
import { IProcessingStep, IPSAddtitionalProcessingStep, IPSSelectOption, ISummaryItem } from '@/types/processingSteps'
import CodeStringInput from '@/components/funding_code/shared/CodeStringInput.vue'
import SingleSelectField from '../commons/fields/SingleSelectField.vue'
import { API_URLS } from '@/utils/helpers'
import axios from 'axios'
import EditAdditionalStep from '@/components/processing_steps/additional_steps/EditAdditionalStep.vue'
import IntegerField from '../commons/fields/IntegerField.vue'
import AccountPublishFundingApplicationForm from './completition_forms/AccountPublishFundingApplicationForm.vue'
import AdminCompletition from './completition_forms/AdminCompletition.vue'
import { IFundingApplication, IPSFundingApplicationAdminCompletitionForm } from '@/types/fundingApplication'
import AccountAccepted from './completition_forms/AccountAccepted.vue'
import AccountInReview from './completition_forms/AccountInReview.vue'
import CharField from '../commons/fields/CharField.vue'
import { TGenericObject } from '@/types/base'
import AccountRejected from './completition_forms/AccountRejected.vue'
import ConfirmModal from '../commons/modals/ConfirmModal.vue'
import RedirectToFormBtn from '@/components/funding_code/RedirectToFormBtn.vue'
import BreadcrumbMixin from '../commons/BreadcrumbMixin.vue'
import AdditionalStepMixin from '../additional_steps/AdditionalStepMixin.vue'
import LocaleMixin from '@/mixins/LocaleMixin'
import TextFieldEditor from '../commons/fields/TextFieldEditor.vue'
import ProjectForeignOrgaInfo from '../project/forms/ProjectForeignOrgaInfo.vue'
import DetailedFinancingNeeds from '../commons/forms/DetailedFinancingNeeds.vue'
import NewCurrencyField from '../commons/fields/NewCurrencyField.vue'

@Component({
  components: {
    StepContainer,
    FormContainer,
    CodeStringInput,
    CharField,
    SingleSelectField,
    EditAdditionalStep,
    IntegerField,
    AccountPublishFundingApplicationForm,
    AdminCompletition,
    AccountAccepted,
    AccountInReview,
    AccountRejected,
    ConfirmModal,
    RedirectToFormBtn,
    TextFieldEditor,
    ProjectForeignOrgaInfo,
    DetailedFinancingNeeds,
    NewCurrencyField
  }
})
export default class FundingApplication extends Mixins(ProcessingStepMixin, ProjectAdditionalStepMixin, BreadcrumbMixin, AdditionalStepMixin, LocaleMixin) {
  availableProjects!: IPSSelectOption[]
  fundingApplication: IFundingApplication = {
    slug: '',
    reference_number: '',
    final_status: 'Draft',
    external_document_href: null,
    in_foreign_country: false,
    description: '',
    realization_time_in_days: null,
    initial_situation: null,
    planned_activities: null,
    risk_factors: null,
    goals_and_impact: null,
    goal_in_cents: 0,
    detailed_financing_need: null,
    created_at: '',
    funding_code: {
      title: '',
      description: '',
      code_string: '',
      form_link: '',
      form_link_desc: '',
      needs_reference_number: false,
      start_date: null,
      end_date: null,
      logos: []
    },
    additional_steps: [],
    project: {
      slug: '',
      title: ''
    },
    show_application_info_step: false,
    show_need_step: false
  }

  formErrors: TGenericObject = {
    reference_number: ''
  }

  hasProject = false
  termsAccepted = false
  publishModalOpen = false

  summaryItems: ISummaryItem[] = []

  firstStep: IProcessingStep = {
    key: "basic",
    title: this.$gettext('Basic information'),
    completed: false,
    required: true,
    active: true,
    buttons: {
      save: true,
      saveAndNext: true
    }
  }

  externalFormStep: IProcessingStep = {
    key: "external_form",
    title: this.$pgettext('Additional information', 'FundingApplication'),
    completed: false,
    required: true,
    active: true,
    buttons: {
      save: true,
      saveAndNext: true
    }
  }

  needStep: IProcessingStep = {
    key: "need",
    title: this.$gettext('Application need'),
    completed: false,
    required: true,
    active: false,
    buttons: {
      save: true,
      saveAndNext: true
    }
  }

  applicationInfoStep: IProcessingStep = {
    key: "application_info",
    title: this.$gettext('Application information'),
    completed: false,
    required: true,
    active: false,
    buttons: {
      save: true,
      saveAndNext: true
    }
  }

  completitionStep: IProcessingStep = {
    key: "completition",
    title: this.$gettext('Completition'),
    completed: false,
    required: true,
    active: false,
    buttons: {
      save: false,
      saveAndNext: false
    }
  }

  adminCompletitionForm: IPSFundingApplicationAdminCompletitionForm = {
    confirmation_of_receipt_status: '',
    donation_agreement_status: '',
    status: '',
    content_review_status: '',
    review_status: '',
    final_status: ''
  }

  // there are 2 versions of applications
  // see funding application model
  get isExternalForm (): boolean {
    return !!this.fundingApplication?.funding_code.form_link && this.fundingApplication?.funding_code.form_link.length > 0
  }

  // see funding application model
  get externalFormHasBeenCreated (): boolean {
    if (!this.isExternalForm) {
      return false
    }
    return !!this.fundingApplication?.external_document_href && this.fundingApplication?.external_document_href.length > 0
  }

  get needsReferenceNumber (): boolean {
    return this.fundingApplication.funding_code.needs_reference_number
  }

  get inForeignCountry (): boolean {
    return this.fundingApplication.in_foreign_country
  }

  get publishCheckBoxHtml (): string {
    return this.$gettext('I agree to the <a href="https://www.hausdesstiftens.org/rechtliches/nutzungsbedingungen/">terms of use</a> and the <a href="https://www.hausdesstiftens.org/rechtliches/datenschutz/">privacy policy</a>. By clicking on "Submit application", your application will be forwarded for review. You will receive a confirmation by e-mail.')
  }

  get isCreate (): boolean {
    return window.location.href.endsWith('create/')
  }

  get applicationInfoHint (): string {
    if (this.inForeignCountry) {
      return this.$gettext("Your details have been automatically transferred from the project information.")
    }
    return this.$gettext("You can use the information from the project or formulate it in more detail for the application. If you would like to specify the project in more detail, please adjust the description text accordingly.")
  }

  get needHint (): string {
    if (this.inForeignCountry) {
      return this.$gettext("Your details have been automatically transferred from the project information.")
    }
    return this.$gettext("Please enter the amount requested in euros (without thousand separators). If the amount remains unchanged, simply click on 'Save and continue'.")
  }

  get projectSelectionHint (): string {
    return this.$gettext('Please select a project for which you would like to submit an application. Only projects that have been fully created and published in the <a class="text-hds-highlight" href="/account/projects-new/">"Projects"</a> section will appear in the list. If your project is not shown, please create and publish it under <a class="text-hds-highlight" href="/account/projects-new/">"Projects"</a> first. It will then automatically appear in this selection.')
  }

  get baseInformationCompleted (): boolean {
    const hasFundingNumber = this.fundingApplication.funding_code.code_string?.length
    if (this.needsReferenceNumber) {
      return hasFundingNumber && this.hasProject && this.fundingApplication.reference_number?.length > 0
    }
    return hasFundingNumber && this.hasProject
  }

  get isDraft (): boolean {
    // todo
    return this.fundingApplication.final_status === 'Draft'
  }

  get isRejected (): boolean {
    // todo
    return this.fundingApplication.final_status === 'Rejected'
  }

  get isInReview (): boolean {
    // todo
    return this.fundingApplication.final_status === 'In review'
  }

  get isAccepted (): boolean {
    // todo
    return this.fundingApplication.final_status === 'Accepted'
  }

  get isComplete (): boolean {
    if (!this.fundingApplication.funding_code.code_string) { return false }
    if (!this.fundingApplication.project.slug) { return false }
    return this.allStepsCompleted()
  }

  get tableUrl (): string {
    if (this.isSiteAdmin) {
      return '/site-admin/funding-applications/'
    }
    return '/account/funding-applications/'
  }

  get listUrl (): string {
    return this.isSiteAdmin ? API_URLS.FUNDING_APPLICATION.LIST : API_URLS.FUNDING_APPLICATION.LIST + '?own'
  }

  get retrieveUrl (): string {
    return this.isSiteAdmin ? API_URLS.FUNDING_APPLICATION.RETRIEVE(this.fundingApplication.slug) : API_URLS.FUNDING_APPLICATION.RETRIEVE(this.fundingApplication.slug) + '?own'
  }

  get publishUrl (): string {
    return this.isSiteAdmin ? API_URLS.FUNDING_APPLICATION.PUBLISH(this.fundingApplication.slug) : API_URLS.FUNDING_APPLICATION.PUBLISH(this.fundingApplication.slug) + '?own'
  }

  get projectName (): string {
    return this.fundingApplication?.project?.title || ""
  }

  get fundingCodeEndDateMessage (): string {
    if (!this.fundingApplication?.funding_code?.end_date) {
      return ''
    }
    const endDate = this.formatDateOnlyDay(this.fundingApplication.funding_code.end_date)
    return this.$gettext('Please note that the funding code is only valid until {endDate}. Please submit your application on time.').replace('{endDate}', endDate)
  }

  setSummaryItems (): void {
    const baseItems = [
      {
        title: this.$gettext('Antrag'),
        items: [
          {
            title: this.$gettext('Funding program'),
            values: [this.fundingApplication.funding_code.title]
          },
          {
            title: this.$gettext('Funding number'),
            values: [this.fundingApplication.funding_code.code_string]
          },
          {
            title: this.$gettext('Project\'s name'),
            values: [this.fundingApplication.project.title]
          },
          ...(
            this.hasNeedAndApplicationInfoStep
              ? [{
                  title: this.$gettext('Description'),
                  values: [
                    `<a class="text-hds-highlight" href="/account/funding-applications/${this.fundingApplication.slug}/update/?step=application_info">${this.$gettext("See application information step")}</a>`
                  ]
                }]
              : []
          )
        ]
      }
    ]

    const additionalFieldItems = []
    for (const addStep of this.fundingApplication.additional_steps) {
      if (!addStep.additional_step || !addStep.additional_step.additional_fields) continue
      const items = []
      for (const field of addStep.additional_step.additional_fields) {
        let value = field.value?.value
        if (this.isDateField(field) && value) {
          value = typeof value === 'string'
            ? this.formatDateOnlyDay(value)
            : (value.toLocaleString ? this.formatDateOnlyDay(value.toLocaleString()) : this.formatDateOnlyDay(value))
        }
        if (this.isDocumentField(field) && value?.photos) {
          value = value.photos.map(photo => photo.name || photo.url).join(', ')
        }
        if (this.isSelectField(field) && field.possible_values) {
          const selected = field.possible_values.find(opt => opt.value === value)
          value = selected ? selected.text : value
        }
        if (Array.isArray(value)) {
          value = value.join(', ')
        }
        if (this.isCheckboxField(field)) {
          value = value ? this.$gettext('Yes') : this.$gettext('No')
        }
        items.push({
          title: field.title,
          values: [value]
        })
      }
      additionalFieldItems.push({
        title: addStep.title,
        items
      })
    }

    this.summaryItems = [
      ...baseItems,
      ...additionalFieldItems
    ]
  }

  openPublishModal (): void {
    this.publishModalOpen = true
  }

  redirectToTable (): void {
    window.location.href = this.tableUrl
  }

  allStepsCompleted (): boolean {
    return this.steps
      .filter((step: IProcessingStep) => step.key !== 'completition')
      .every((step: IProcessingStep) => step.completed)
  }

  async mounted (): Promise<void> {
    // todo set termsAccepted = true if orga has published
    if (!this.isCreate) {
      this.setSlugFromUrl()
      await this.fetchFundingApplicationData()
    }
    await this.fetchAvailableProjects()

    const instanceType = this.$gettext('Funding applications')
    const urlPrefix = this.isSiteAdmin ? '/site-admin/' : '/account/'
    const editUrl = urlPrefix + "funding-applications/" + this.fundingApplication.slug + "/update/"

    this.updateBreadCrumbs(
      this.tableUrl,
      "/account/funding-applications/create/",
      this.isCreate,
      this.fundingApplication.funding_code.title,
      editUrl,
      instanceType,
      "submission-of-application-icon"
    )

    this.setSteps()
    this.setActiveStep(null)
    this.setSummaryItems()
    this.checkAndValidateSteps()
  }

  setSlugFromUrl (): void {
    this.fundingApplication.slug = window.location.pathname.split("funding-applications/")[1].split("/update/")[0]
  }

  setDetailedFinancingNeedsForm (fundingApplication: IFundingApplication): void {
    const project = fundingApplication
    if (!project || !project.detailed_financing_need) {
      return
    }
    this.fundingApplication.detailed_financing_need = {
      investments: {
        need_in_cents: (parseFloat(project.detailed_financing_need.investments.need_in_cents) / 100).toFixed(2),
        description: project.detailed_financing_need.investments.description
      },
      operating_costs: {
        need_in_cents: (parseFloat(project.detailed_financing_need.operating_costs.need_in_cents) / 100).toFixed(2),
        description: project.detailed_financing_need.operating_costs.description
      },
      staff_costs: {
        need_in_cents: (parseFloat(project.detailed_financing_need.staff_costs.need_in_cents) / 100).toFixed(2),
        description: project.detailed_financing_need.staff_costs.description
      },
      other_costs: {
        need_in_cents: (parseFloat(project.detailed_financing_need.other_costs.need_in_cents) / 100).toFixed(2),
        description: project.detailed_financing_need.other_costs.description
      }
    }
  }

  setFundingApplication (data: TGenericObject): void {
    this.fundingApplication = data as IFundingApplication
    this.fundingApplication.additional_steps = this.createAdditonalSteps(data.additional_steps)
    this.fundingApplication.funding_code.code_string = data.funding_code.code_string.replace("F", "")
    this.fundingApplication.funding_code.logos = data.funding_code.logos || []
    this.hasProject = !!data.project
    this.fundingApplication.project = data.project ?? { slug: '', title: '' }
    this.setDetailedFinancingNeedsForm(this.fundingApplication)
    if (this.isSiteAdmin) {
      this.adminCompletitionForm = {
        confirmation_of_receipt_status: data.confirmation_of_receipt_status,
        donation_agreement_status: data.donation_agreement_status,
        status: data.status,
        review_status: data.review_status,
        final_status: data.final_status,
        content_review_status: data.content_review_status
      }
    }
  }

  async fetchFundingApplicationData (): Promise<void> {
    const url = this.retrieveUrl
    await axios.get(url)
      .then(response => {
        const data = response.data
        this.setFundingApplication(data)
        this.setSummaryItems()
      })
      .catch(() => {
        this.showError({
          title: this.$gettext('Error'),
          message: this.$gettext('Failed to fetch funding application data. Please try again.')
        })
      })
  }

  handleFilesUploaded (): void {
    this.fetchFundingApplicationData()
  }

  async publishFundingApplication (): Promise<void> {
    if (!this.termsAccepted) {
      this.showError({
        title: this.$gettext('Error'),
        message: this.$gettext('You must accept the terms and privacy policy to publish the funding application.')
      })
      return
    }
    await axios.post(this.publishUrl)
      .then(() => {
        this.showSuccessMsg({
          title: this.$gettext('Success'),
          message: this.$gettext('Funding application published successfully.')
        })
        this.redirectToTable()
      })
      .catch(error => {
        let message = this.$gettext('Failed to publish funding application. Please try again.')
        if (error.response && error.response.data && error.response.data.error) {
          message = error.response.data.error
        }
        this.showError({
          title: this.$gettext('Error'),
          message
        })
      })
  }

  async fetchAvailableProjects (): Promise<void> {
    // todo use a promise all, try page size etc.
    // some user have >100 projects, so we need to paginate here
    let projects: { title: string, slug: string }[] = []
    const filterParams = 'mixed_status=live_funding_application'
    let nextUrl = this.isSiteAdmin
      ? API_URLS.PROJECTS.MINIMAL_LIST + '?' + filterParams
      : API_URLS.PROJECTS.ACCOUNT_LIST + '?own&' + filterParams

    try {
      while (nextUrl) {
        const response = await axios.get(nextUrl)
        const data = response.data
        if (Array.isArray(data.results)) {
          projects = projects.concat(data.results)
          nextUrl = data.next || (data.links && data.links.next) || null
        } else if (Array.isArray(data)) {
          projects = projects.concat(data)
          nextUrl = null
        } else {
          nextUrl = null
        }
      }
      this.availableProjects = projects.map(project => ({
        text: project.title,
        value: project.slug
      }))
      // if there is already a project, but its status has changed for any reason, so it cant be selected anymore, it still needs to be displayed
      const currentProject = this.fundingApplication.project
      if (
        currentProject &&
        currentProject.slug &&
        !this.availableProjects.some(p => p.value === currentProject.slug)
      ) {
        this.availableProjects.push({
          text: currentProject.title,
          value: currentProject.slug
        })
      }
    } catch (e) {
      this.showError(null)
    }
  }

  async saveAndNextBtnClicked (): Promise<void> {
    const saved = await this.saveBtnClicked()
    if (saved) {
      this.checkAndValidateSteps()
      this.gotoNextStepOrFinish(this.steps, this.activeStep, "funding_application")
    }
  }

  async updateGoal (): Promise<boolean> {
    if (this.inForeignCountry || !this.isDraft) {
      return true
    }
    const saved = await axios.patch(API_URLS.FUNDING_APPLICATION.UPDATE_GOAL(this.fundingApplication.slug), {
      goal_in_cents: this.fundingApplication.goal_in_cents
    }).then(() => {
      this.showSuccessMsg({
        title: this.$gettext('Success'),
        message: this.$gettext('Financing need updated successfully.')
      })
      return true
    }).catch(error => {
      this.showError(null)
      if (error.response && error.response.data) {
        const errors = error.response.data
        const message = typeof errors === 'string'
          ? errors
          : Object.values(errors).map(v => Array.isArray(v) ? (v as string[]).join(', ') : (v as string)).join('\n')
        this.showError({
          title: this.$gettext('Error'),
          message
        })
      }
      return false
    })
    return saved || false
  }

  async updateApplicationInfo (): Promise<boolean> {
    if (this.inForeignCountry || !this.isDraft) {
      return true
    }
    const saved = await axios.patch(API_URLS.FUNDING_APPLICATION.UPDATE_APPLICATION_INFO(this.fundingApplication.slug), {
      description: this.fundingApplication.description
    }).then(() => {
      this.showSuccessMsg({
        title: this.$gettext('Success'),
        message: this.$gettext('Application information updated successfully.')
      })
      return true
    }).catch(error => {
      this.showError(null)
      if (error.response && error.response.data) {
        const errors = error.response.data
        const message = typeof errors === 'string'
          ? errors
          : Object.values(errors).map(v => Array.isArray(v) ? (v as string[]).join(', ') : (v as string)).join('\n')
        this.showError({
          title: this.$gettext('Error'),
          message
        })
        return false
      }
    })
    return saved || false
  }

  async saveBtnClicked (): Promise<boolean> {
    let saved = false
    if (this.activeStep.key === 'basic') {
      saved = await this.updateBaseInformation()
      await this.fetchFundingApplicationData()
    } else if (this.activeStep.key === 'need') {
      saved = await this.updateGoal()
    } else if (this.activeStep.key === 'application_info') {
      saved = await this.updateApplicationInfo()
      await this.fetchFundingApplicationData()
    } else if (this.isIPSAdditionalProcessingStep(this.activeStep)) {
      saved = await this.saveAdditionalStep((this.activeStep as IPSAddtitionalProcessingStep), this.fundingApplication.slug)
    } else if (this.isSiteAdmin && this.activeStep.key === 'completition') {
      saved = await this.adminChangeStatus()
    }
    return saved
  }

  async createApplication (): Promise<void> {
    // todo nuke the f
    const payload = {
      funding_number: 'F' + this.fundingApplication.funding_code.code_string
    }
    await axios.post(this.listUrl, payload)
      .then((response) => {
        this.showSuccessMsg({
          title: this.$gettext('Success'),
          message: this.$gettext('Funding application created successfully.')
        })
        const slug = response.data.slug
        const url = `/account/funding-applications/${slug}/update/`
        window.location.href = url
      })
      .catch((error) => {
        const statusCode = error.response ? error.response.status : null

        if (statusCode === 404) {
          this.showError({
            title: this.$gettext('Error'),
            message: this.$gettext('Funding code not found. Please check the funding number and try again.')
          })
        } else if (statusCode === 400 && error.response && error.response.data) {
          const errors = error.response.data
          const message = typeof errors === 'string'
            ? errors
            : Object.values(errors).map(v => Array.isArray(v) ? (v as string[]).join(', ') : (v as string)).join('\n')
          this.showError({
            title: this.$gettext('Error'),
            message
          })
        } else {
          this.showError({
            title: this.$gettext('Error'),
            message: this.$gettext('Failed to create funding application. Please try again.')
          })
        }
      })
  }

  gotoStep (step: IProcessingStep): void {
    this.checkAndValidateStep(this.activeStep)
    if (this.baseInformationCompleted) {
      this.setActiveStep(step)
    } else {
      this.showError(null)
    }
  }

  checkAndValidateSteps (): void {
    this.steps.forEach((step: IProcessingStep) => {
      this.checkAndValidateStep(step)
    })
  }

  async checkAndValidateStep(step: IProcessingStep) {
    if (step.required === false) {
      this.setAdditionalStepValid(step)
      return
    }
    if (step.key === "basic") {
      if (this.baseInformationCompleted) {
        this.setAdditionalStepValid(step)
      } else {
        this.setAdditionalStepInvalid(step)
      }
    } else if (step.key === "need") {
      if (this.inForeignCountry || this.fundingApplication.goal_in_cents > 0) {
        this.setAdditionalStepValid(step)
      } else {
        this.setAdditionalStepInvalid(step)
      }
    } else if (step.key === "application_info") {
      if (this.inForeignCountry || (this.fundingApplication.description && this.fundingApplication.description.length > 0)) {
        this.setAdditionalStepValid(step)
      } else {
        this.setAdditionalStepInvalid(step)
      }
    } else if (step.key === "external_form") {
      if (this.fundingApplication.external_document_href) {
        this.setAdditionalStepValid(step)
      } else {
        this.setAdditionalStepInvalid(step)
      }
    }
  }

  async saveAdditionalStep (step: IPSAddtitionalProcessingStep, projectSlug: string): Promise<boolean> {
    if (!this.isDraft) {
      this.showError({
        title: this.$gettext('Error'),
        message: this.$gettext('You cannot update the data of the funding application, because it is not in draft status.')
      })
      return false
    }
    let saved = false
    const additionalStep = step.additional_step
    const apiUrl = additionalStep.api_url
    const additionalFields = additionalStep.additional_fields
    let contentType = null
    const data = {
      content_type: null,
      object_slug: projectSlug
    }
    for (const field of additionalFields) {
      // For photos / files - If a field has no content type, the field is an photo album, so it is not supposed to be in the request data, because the file is uploaded directly in photocomponent
      if (!field.value.content_type) {
        continue
      }
      contentType = field.value.content_type ? field.value.content_type : contentType
      data[field.slug] = field.value.value
    }
    data.content_type = contentType
    await axios.put(apiUrl, data).then(() => {
      saved = true
      this.setAdditionalStepValid(step)
      const message = additionalStep.title + " " + this.$gettext('saved successfully')
      this.showSuccessMsg({ title: 'Success!', message })
    }).catch(error => {
      this.showError(null)
      this.steps = this.steps.map(step => {
        if ('form_errors' in step) {
          (step as IPSAddtitionalProcessingStep).form_errors = error.response.data
        }
        return step
      })
    })
    return saved
  }

  async verifyReferenceNumber (referenceNumber: string): Promise<boolean> {
    // todo get url etc from hds
    const url = API_URLS.FUNDING_APPLICATION.VERIFY_REFERENCE_ID
    if (!referenceNumber || referenceNumber.length === 0) {
      this.showRequiredFieldsError()
      this.$nextTick(() => {
        this.formErrors.reference_number = this.emptyFieldErrorMessage
      })
      return false
    }

    try {
      const response = await axios.post(url, { reference_id: referenceNumber }, { timeout: 5000 })
      if (response.data && response.data.found === true) {
        return true
      } else {
        this.showError({
          title: this.$gettext('Error'),
          message: this.$gettext('The reference ID you entered is invalid. Please check your entry.')
        })
        return false
      }
    } catch (error) {
      let message = this.$gettext('The reference ID could not be validated. Please try again or contact support.')
      if (typeof error === 'object' && error !== null && 'code' in error) {
        if ((error as { code?: string }).code === 'ECONNABORTED') {
          message = this.$gettext('The request timed out. Please try again later.')
        }
      }
      this.showError({
        title: this.$gettext('Error'),
        message
      })
      return false
    }
  }

  resetFormErrors (): void {
    this.formErrors = {
      reference_number: ''
    }
  }

  async updateBaseInformation (): Promise<boolean> {
    if (!this.isDraft) {
      return true
    }
    this.resetFormErrors()
    const needsReferenceNumber = this.needsReferenceNumber
    const referenceNumber = this.fundingApplication.reference_number
    if (needsReferenceNumber) {
      const validReferenceNumber = await this.verifyReferenceNumber(referenceNumber)
      if (!validReferenceNumber) {
        return false
      }
    }
    const url = this.retrieveUrl
    const saved = await axios.put(url, {
      project: this.fundingApplication.project.slug,
      reference_number: referenceNumber
    })
      .then(() => {
        this.showSuccessMsg({
          title: this.$gettext('Success'),
          message: this.$gettext('Funding number saved successfully.')
        })
        this.hasProject = true
        return true
      })
      .catch((error) => {
        let message = this.$gettext('Failed to save funding number. Please try again.')
        if (error.response && error.response.data) {
          const errors = error.response.data
          message = Object.values(errors).map((v: TGenericObject) => Array.isArray(v) ? v.join(', ') : v).join('\n')
        }
        this.showError({
          title: this.$gettext('Error'),
          message
        })
        return false
      })
    return saved
  }

  async adminChangeStatus (): Promise<boolean> {
    let saved = false
    const payload = this.adminCompletitionForm
    await axios.post(API_URLS.FUNDING_APPLICATION.CHANGE_STATUS(this.fundingApplication.slug), payload)
      .then(() => {
        this.showSuccessMsg({
          title: this.$gettext('Success'),
          message: this.$gettext('Funding application status updated successfully.')
        })
        saved = true
      })
      .catch((error) => {
        let message = this.$gettext('Failed to update funding application status. Please try again.')
        if (error.response && error.response.data) {
          // Collect all error messages from the serializer
          const errors = error.response.data
          message = Object.values(errors).map((v: TGenericObject) => Array.isArray(v) ? v.join(', ') : v).join('\n')
        }
        this.showError({
          title: this.$gettext('Error'),
          message
        })
      })
    return saved
  }

  setCompletitionStep (): void {
    if (!this.isCreate) {
      if (this.fundingApplication.final_status !== 'Draft') {
        this.completitionStep.completed = true
      }
    }
  }

  get hasNeedAndApplicationInfoStep (): boolean {
    // todo add a schema to the funding application model or migrate
    return this.fundingApplication.show_need_step === true && this.fundingApplication.show_application_info_step === true
  }

  setSteps (): void {
    if (this.isSiteAdmin) {
      this.completitionStep.buttons.save = true
      this.completitionStep.buttons.saveAndNext = true
    }
    this.setCompletitionStep()

    this.steps = [
      this.firstStep,
      ...(this.isExternalForm ? [this.externalFormStep] : this.fundingApplication.additional_steps),
      ...(this.hasNeedAndApplicationInfoStep ? [this.needStep, this.applicationInfoStep] : []),
      this.completitionStep
    ]
  }

  async onExternalDocumentCreated (externalDocumentHref: string): Promise<void> {
    const url = API_URLS.FUNDING_APPLICATION.ADD_EXTERNAL_DOCUMENT_HREF(this.fundingApplication.slug) + '?own'
    const payload = {
      external_document_href: externalDocumentHref
    }
    await axios.post(url, payload)
      .then(() => {
        this.fundingApplication.external_document_href = externalDocumentHref
        this.showSuccessMsg({
          title: this.$gettext('Success'),
          message: this.$gettext('External document created successfully.')
        })
      })
      .catch(() => {
        this.showError({
          title: this.$gettext('Error'),
          message: this.$gettext('Failed to create external document. Please try again.')
        })
      })
  }
}
